import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["colRender", "rowData", "cellData"],
  _excluded2 = ["id", "columns", "data", "caption", "renderAboveHeader", "dragDropConfig"];
import React, { useRef, useCallback, useEffect } from 'react';
import { DragDropContext } from 'react-dnd';
import ReactDnDHTML5Backend from 'react-dnd-html5-backend';
import { Table as InstUITable } from '@instructure/ui-table';
import { View } from '@instructure/ui-view';
import { CustomDragLayer } from "../../dragdrop/CustomDragLayer/index.js";
import DragDropWrapper from "../../dragdrop/DragDropWrapper/index.js";
import { DragDropContainer } from "../../dragdrop/DragDropContainer/index.js";
import { Cell } from "../Cell/index.js";
import { Row } from "../Row/index.js";
import { RowHeader } from "../RowHeader/index.js";
import { ColumnHeader } from "../ColumnHeader/index.js"; // DataCell keeps the Cell child arrow function out of the hot render
// path. React.memo compares colRender/rowData/cellData by reference; when columns are
// reordered those values don't change, so Cell never receives new props and skips render.
var DataCellInner = function DataCellInner(_ref) {
  var colRender = _ref.colRender,
    rowData = _ref.rowData,
    cellData = _ref.cellData,
    cellProps = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(Cell, cellProps, function (focused) {
    return colRender(cellData, rowData, focused);
  });
};
var DataCell = /*#__PURE__*/React.memo(DataCellInner);
export var TableComponent = function TableComponent(_ref2) {
  var id = _ref2.id,
    columns = _ref2.columns,
    data = _ref2.data,
    caption = _ref2.caption,
    renderAboveHeader = _ref2.renderAboveHeader,
    dragDropConfig = _ref2.dragDropConfig,
    tableProps = _objectWithoutProperties(_ref2, _excluded2);
  var tableRef = useRef(null);
  var columnsRef = useRef(columns);

  // Keep columnsRef in sync with latest columns
  useEffect(function () {
    columnsRef.current = columns;
  }, [columns]);
  var getCellElement = useCallback(function (rowIndex, colKey) {
    if (!tableRef.current) return null;
    var selector = "[data-row-index=\"".concat(rowIndex, "\"][data-col-key=\"").concat(colKey, "\"]");
    return tableRef.current.querySelector(selector);
  }, []);
  var focusCell = useCallback(function (rowIndex, colKey) {
    var cell = getCellElement(rowIndex, colKey);
    if (cell) {
      if (rowIndex === -1) {
        var focusable = cell.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusable) {
          focusable.focus();
          return;
        }
      }
      cell.focus();
    }
  }, [getCellElement]);
  var handleKeyDown = useCallback(function (event) {
    // Allow arrow key navigation from the cell itself or from buttons within the cell
    // (e.g., header options menu), but prevent navigation from other interactive elements
    // (e.g., chart components that have their own keyboard navigation)
    if (event.target !== event.currentTarget) {
      var _target = event.target;
      var isButton = _target.tagName === 'BUTTON' || _target.getAttribute('role') === 'button';
      if (!isButton) {
        return;
      }
    }
    var target = event.currentTarget;
    var rowIndex = parseInt(target.dataset.rowIndex || '0', 10);
    var colKey = target.dataset.colKey || '';
    var key = event.key;
    var newRowIndex = rowIndex;
    var newColKey = colKey;
    switch (key) {
      case 'ArrowUp':
        {
          event.preventDefault();
          // Allow moving from data (0+) to header (-1) to above-header (-2)
          // Above-header row exists only if renderAboveHeader is provided
          var minRow = renderAboveHeader ? -2 : -1;
          newRowIndex = Math.max(minRow, rowIndex - 1);
          break;
        }
      case 'ArrowDown':
        event.preventDefault();
        // Allow moving from above-header (-2) to header (-1) to data rows (0+)
        newRowIndex = Math.min(data.length - 1, rowIndex + 1);
        break;
      case 'ArrowLeft':
        {
          var _columnsRef$current$n;
          event.preventDefault();
          var currentColIndex = columnsRef.current.findIndex(function (col) {
            return col.key === colKey;
          });
          var newColIndex = Math.max(0, currentColIndex - 1);
          newColKey = ((_columnsRef$current$n = columnsRef.current[newColIndex]) === null || _columnsRef$current$n === void 0 ? void 0 : _columnsRef$current$n.key) || colKey;
          break;
        }
      case 'ArrowRight':
        {
          var _columnsRef$current$_;
          event.preventDefault();
          var _currentColIndex = columnsRef.current.findIndex(function (col) {
            return col.key === colKey;
          });
          var _newColIndex = Math.min(columnsRef.current.length - 1, _currentColIndex + 1);
          newColKey = ((_columnsRef$current$_ = columnsRef.current[_newColIndex]) === null || _columnsRef$current$_ === void 0 ? void 0 : _columnsRef$current$_.key) || colKey;
          break;
        }
      default:
        return;
    }
    if (newRowIndex !== rowIndex || newColKey !== colKey) {
      focusCell(newRowIndex, newColKey);
    }
  }, [data.length, focusCell, renderAboveHeader]);
  var renderColHeader = useCallback(function (col, colIndex, draggableIndex, dragDropConfig) {
    var colHeaderProps = _objectSpread({
      id: col.key,
      width: col.width,
      isSticky: col.isSticky,
      'data-cell-id': "header-".concat(col.key),
      'data-row-index': -1,
      'data-col-key': col.key,
      tabIndex: 0,
      onKeyDown: handleKeyDown
    }, col.colHeaderProps);
    var dragLabel = col.dragLabel || col.key;
    return col.draggable && dragDropConfig ? /*#__PURE__*/React.createElement(DragDropWrapper, Object.assign({
      key: col.key,
      component: ColumnHeader,
      type: dragDropConfig.type,
      itemId: col.key,
      index: draggableIndex,
      dragLabel: dragLabel,
      onMove: dragDropConfig.onMove,
      onDragEnd: dragDropConfig.onDragEnd
    }, colHeaderProps), typeof col.header === 'function' ? col.header() : col.header) : /*#__PURE__*/React.createElement(ColumnHeader, Object.assign({
      key: col.key
    }, colHeaderProps), typeof col.header === 'function' ? col.header() : col.header);
  }, [handleKeyDown]);
  var renderHeader = function renderHeader() {
    var isDragDropEnabled = dragDropConfig === null || dragDropConfig === void 0 ? void 0 : dragDropConfig.enabled;
    if (isDragDropEnabled && dragDropConfig) {
      // Pre-compute draggable indices for all columns
      var draggableIndices = new Map();
      var draggableCounter = 0;
      columns.forEach(function (col, idx) {
        if (col.draggable) {
          draggableIndices.set(idx, draggableCounter);
          draggableCounter++;
        }
      });
      return /*#__PURE__*/React.createElement(InstUITable.Head, null, /*#__PURE__*/React.createElement(React.Fragment, null, renderAboveHeader && renderAboveHeader(columns, handleKeyDown), /*#__PURE__*/React.createElement(DragDropContainer, {
        type: dragDropConfig.type,
        onDragLeave: dragDropConfig.onDragLeave
      }, function (connectDropTarget) {
        return /*#__PURE__*/React.createElement(Row, {
          setRef: function setRef(el) {
            if (el instanceof HTMLElement) {
              connectDropTarget(el);
            }
          }
        }, columns.map(function (col, colIndex) {
          var _draggableIndices$get;
          var currentDraggableIndex = (_draggableIndices$get = draggableIndices.get(colIndex)) !== null && _draggableIndices$get !== void 0 ? _draggableIndices$get : 0;
          return renderColHeader(col, colIndex, currentDraggableIndex, dragDropConfig);
        }));
      })));
    }
    return /*#__PURE__*/React.createElement(InstUITable.Head, null, /*#__PURE__*/React.createElement(React.Fragment, null, renderAboveHeader && renderAboveHeader(columns, handleKeyDown), /*#__PURE__*/React.createElement(Row, null, columns.map(function (col, colIndex) {
      return renderColHeader(col, colIndex, 0);
    }))));
  };
  var renderBody = function renderBody() {
    return /*#__PURE__*/React.createElement(InstUITable.Body, null, data.map(function (row, rowIndex) {
      return /*#__PURE__*/React.createElement(Row, {
        key: rowIndex
      }, columns.map(function (col) {
        var props = _objectSpread({
          id: "".concat(rowIndex, "-").concat(col.key),
          'data-cell-id': "cell-".concat(rowIndex, "-").concat(col.key),
          'data-row-index': rowIndex,
          'data-col-key': col.key,
          tabIndex: 0,
          onKeyDown: handleKeyDown,
          isSticky: col.isSticky
        }, col.cellProps);
        if (col.isRowHeader) {
          return /*#__PURE__*/React.createElement(RowHeader, Object.assign({
            key: col.key
          }, props), col.render ? col.render(row[col.key], row) : row[col.key]);
        }
        if (col.render) {
          return /*#__PURE__*/React.createElement(DataCell, Object.assign({
            key: col.key,
            colRender: col.render,
            rowData: row,
            cellData: row[col.key]
          }, props));
        }
        return /*#__PURE__*/React.createElement(Cell, Object.assign({
          key: col.key
        }, props), row[col.key]);
      }));
    }));
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, (dragDropConfig === null || dragDropConfig === void 0 ? void 0 : dragDropConfig.enabled) && /*#__PURE__*/React.createElement(CustomDragLayer, {
    renderItem: function renderItem(item) {
      return (item === null || item === void 0 ? void 0 : item.label) || 'Dragging...';
    }
  }), /*#__PURE__*/React.createElement(View, {
    as: "div",
    overflowX: "auto",
    padding: "none none small none",
    elementRef: function elementRef(el) {
      if (el instanceof HTMLDivElement) {
        ;
        tableRef.current = el;
      }
    }
  }, /*#__PURE__*/React.createElement(InstUITable, Object.assign({
    id: id,
    caption: caption
  }, tableProps), renderHeader(), renderBody())));
};
export var Table = DragDropContext(ReactDnDHTML5Backend)(TableComponent);